import { useApi } from '@hooks/useApi';
import { Autocomplete, Box, Popper, Stack, styled, TextField, Typography } from '@mui/material';
import { getDecimalPlaces, getLotValue } from '@pages/ocl-trading/utils';
import { LadderSettings } from '@protos/ladders';
import { Product } from '@protos/product';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import { useUserProductsAndTenorsContext } from '@shared/contexts/UserProductsAndTenorsProvider';
import { useCallback, useEffect, useState } from 'react';
import NumberInput from '../NumberInput';

const SmallAutocomplete = styled(Autocomplete)({
  width: '125px',
  borderRadius: 0,
  '& .MuiAutocomplete-inputRoot': {
    padding: 0,
    fontSize: 11,
    height: 25,
    borderRadius: 0,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 20,
    padding: 0,
  },
});

const StyledPopper = styled(Popper)({
  '& .MuiAutocomplete-listbox': {
    fontSize: 11,
  },
});

interface NonSpreadSettingsProps {
  nonSpreadSettings: LadderSettings;
  setNonSpreadSettings: (settings: LadderSettings) => void;
  stopEditing: () => void;
}

export const NonSpreadSettings = ({ nonSpreadSettings, setNonSpreadSettings, stopEditing }: NonSpreadSettingsProps) => {
  const { apiClient } = useApi();
  const user = useUserContext();
  const { userProducts } = useUserProductsAndTenorsContext();

  const allowedOCLTradingProductsForUser = user?.ladder_limits.map(limit => limit.symbol) || [];

  const allTradableUserProducts = userProducts.filter(product => allowedOCLTradingProductsForUser.includes(product.symbol));
  const [selectedValue, setSelectedValue] = useState<Product>();
  const [lotValue, setLotValue] = useState('');

  const onProductChange = useCallback(
    (newProductValue: string) => {
      const product = allTradableUserProducts.find(p => p.symbol === newProductValue);
      if (product) {
        stopEditing();
        setSelectedValue(product);
        setNonSpreadSettings({ ...nonSpreadSettings, symbol: product.symbol, tick_size: getDecimalPlaces(product.tick_size) });
      }
    },
    [allTradableUserProducts, setNonSpreadSettings, nonSpreadSettings]
  );

  const onValidityChange = useCallback(
    (value: number | undefined) => {
      if (!value) return;

      setNonSpreadSettings({ ...nonSpreadSettings, validity: value });
    },
    [nonSpreadSettings, setNonSpreadSettings]
  );

  const onSizeChange = useCallback(
    (value: number | undefined) => {
      if (!value) return;

      setNonSpreadSettings({ ...nonSpreadSettings, size: value });
    },
    [nonSpreadSettings, setNonSpreadSettings]
  );

  useEffect(() => {
    if (!allTradableUserProducts.length) return;

    if (!selectedValue) {
      const value = allTradableUserProducts.find(p => p.symbol === nonSpreadSettings.symbol) || allTradableUserProducts[0];
      setSelectedValue(value);
      setNonSpreadSettings({ ...nonSpreadSettings, tick_size: getDecimalPlaces(value.tick_size) });
    } else if (selectedValue.symbol !== nonSpreadSettings.symbol) {
      setNonSpreadSettings({ ...nonSpreadSettings, symbol: selectedValue.symbol, tick_size: getDecimalPlaces(selectedValue.tick_size) });
    }
  }, [selectedValue, nonSpreadSettings, allTradableUserProducts]);

  useEffect(() => {
    if (!apiClient || !selectedValue) {
      setLotValue('');
      return;
    }
    getLotValueFromSelectedProduct();
  }, [apiClient, selectedValue]);

  const getLotValueFromSelectedProduct = async () => {
    const lot = await getLotValue(selectedValue!, apiClient!);
    setLotValue(lot);
  };

  if (!selectedValue) return null;

  return (
    <Stack display="flex" flex={1} gap={2}>
      <Stack>
        <Typography variant="h6" style={{ marginRight: 'auto' }}>
          {selectedValue.full_name}
        </Typography>
        <Typography fontSize={10}>{lotValue}</Typography>
      </Stack>
      <Box display="flex" flex={1} justifyContent="start" flexWrap="wrap">
        <NumberInput
          value={nonSpreadSettings.validity.toString()}
          onChange={onValidityChange}
          label="Validity (s)"
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '11px',
              '--Input-minHeight': '0',
            },
            '& .MuiInputBase-root': {
              borderRadius: '0px',
              height: 25,
            },
          }}
        />
        <NumberInput
          value={nonSpreadSettings.size.toString()}
          onChange={onSizeChange}
          label="Lots"
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '11px',
              '--Input-minHeight': '0',
            },
            '& .MuiInputBase-root': {
              borderRadius: '0px',
              height: 25,
            },
          }}
        />
        <SmallAutocomplete
          size="small"
          disableClearable
          options={allTradableUserProducts}
          getOptionLabel={(option: unknown) => (option as Product).symbol.toUpperCase()}
          value={selectedValue}
          onChange={(_, newValue) => newValue && onProductChange((newValue as Product).symbol)}
          autoHighlight
          PopperComponent={props => <StyledPopper {...props} />}
          renderInput={params => <TextField {...params} variant="outlined" tabIndex={-1} />}
          isOptionEqualToValue={(option, value) => (option as Product).symbol === (value as Product).symbol}
          sx={{
            '& .MuiAutocomplete-input': { fontSize: 11, paddingLeft: 1.5 },
          }}
        />
      </Box>
    </Stack>
  );
};
